import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCongressTradeTypeandAmountBreakdown {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private selectedDateSelections: string[] = ['today', '1w_ago', '1m_ago', '1y_ago'];
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.attachStyles();
        this.setupEventListeners();
        await this.setupChart(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        const widgetHtml = `
  <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Congress Trade Type and Amount Breakdown
</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
                <div class="item"></div>
                <span></span>
                <div class="item">
                    <div class="legendsCollection">
                        <span class="toggle-date" data-value="today" data-tooltip="Today" style="background-color:#E5484D;"></span>
                        <span class="toggle-date" data-value="1w_ago" data-tooltip="1 Week Ago" style="background-color:#E3B341;"></span>
                        <span class="toggle-date" data-value="1m_ago" data-tooltip="1 Month Ago" style="background-color:#21C0A8;"></span>
                        <span class="toggle-date" data-value="1y_ago" data-tooltip="1 Year Ago" style="background-color:#3B82F6;"></span>
                    </div>
                </div>
            </div>

            <div class="chartDv">
                <div class="chart" id="marketRealtimeChart"></div>
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        // initialize legends/toggles for date selections (multi-select)
        const legendContainer = this.shadowRoot.querySelector('.legendsCollection') as HTMLElement | null;
        if (legendContainer) {
            const toggles = Array.from(legendContainer.querySelectorAll('.toggle-date')) as HTMLElement[];
            toggles.forEach(t => {
                const val = t.getAttribute('data-value') || '';
                const isActive = !!(this.selectedDateSelections && this.selectedDateSelections.indexOf(val) !== -1);
                t.setAttribute('role', 'button');
                t.setAttribute('aria-pressed', isActive.toString());
                t.setAttribute('aria-selected', isActive.toString());
                if (isActive) t.classList.add('active'); else t.classList.remove('active');
                // add a visual focusable outline for keyboard users
                t.tabIndex = 0;
                t.addEventListener('keydown', (ev) => {
                    if ((ev as KeyboardEvent).key === ' ' || (ev as KeyboardEvent).key === 'Enter') {
                        ev.preventDefault();
                        (ev.currentTarget as HTMLElement).click();
                    }
                });
            });

            legendContainer.addEventListener('click', (ev) => {
                const target = (ev.target as HTMLElement).closest('.toggle-date') as HTMLElement | null;
                if (!target) return;
                const val = target.getAttribute('data-value') || '';
                const idx = this.selectedDateSelections.indexOf(val);
                if (idx === -1) {
                    this.selectedDateSelections.push(val);
                } else {
                    if (this.selectedDateSelections.length > 1) {
                        this.selectedDateSelections.splice(idx, 1);
                    } else {
                        return;
                    }
                }

                toggles.forEach(t => {
                    const v = t.getAttribute('data-value') || '';
                    const active = this.selectedDateSelections.indexOf(v) !== -1;
                    t.classList.toggle('active', active);
                    t.setAttribute('aria-pressed', active.toString());
                    t.setAttribute('aria-selected', active.toString());
                });

                this.setupChart(this.config.symbol);
            });
        }
        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(symbol?: string, stockInfo?: any): Promise<void> {
        try {

            const params = new URLSearchParams();
            (this.selectedDateSelections || []).forEach(s => params.append('dateSelections[]', s));
            params.append('showHistoricalAverage', 'false');
            const url = `${APIURL}/v1/discover/treasury-yield-curve?${params.toString()}`;

            const response = await apiGet({
                method: 'GET',
                url,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            this.currentData = await response.json();

            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }

    private updateDisplay(): void {
          const widget = this.shadowRoot.querySelector('.chart') as HTMLElement;

  if (this.currentData) {
    widget?.setAttribute('data-testing-id', 'treasury-yield-curve');
  } else {
    widget?.removeAttribute('data-testing-id');
  }

        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.renderAnalystRating();
    }

    private renderAnalystRating(): void {
        // const container = this.shadowRoot.querySelector('#PriceCamprisonContainer');
        // if (!container) return;
        // const html = ``;
        // container.innerHTML = html;
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;

        if (!container) return;

        const snapshots = this.currentData?.data;
        if (!Array.isArray(snapshots) || snapshots.length === 0) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        let maturities: string[] = [];
        if (
            snapshots.length > 0 &&
            Array.isArray(snapshots[0].data)
        ) {
            maturities = snapshots[0].data.map((p: any) => p.maturity);
        }

        // const isBlack = this.config.theme === 'black';
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');

        const colorList = ['#E5484D', '#E3B341', '#21C0A8', '#3B82F6', '#9C27B0', '#00BCD4'];

        const friendlyLabels: any = { today: 'Today', '1w_ago': '1W Ago', '1m_ago': '1M Ago', '1y_ago': '1Y Ago' };
        const seriesData: any[] = [];
        const legendItems: string[] = [];
        const selected = (this.selectedDateSelections || []);
        const count = Math.min(snapshots.length, selected.length || snapshots.length);
        for (let idx = 0; idx < count; idx++) {
            const snapshot = snapshots[idx];
            const map = new Map<string, number>(
                (snapshot.data || []).map((p: any) => [
                    p.maturity,
                    typeof p.yield === 'number' ? p.yield : Number(p.yield)
                ])
            );

            const values = maturities.map(m => map.get(m) ?? null);

            const selKey = selected[idx] || null;
            const seriesName = selKey ? (friendlyLabels[selKey] || snapshot.date || `Series ${idx + 1}`) : (snapshot.date || `Series ${idx + 1}`);

            const color = colorList[idx % colorList.length];

            seriesData.push({
                name: seriesName,
                type: 'line',
                smooth: false,
                data: values,
                subLabel: snapshot.date || '',
                lineStyle: {
                    color,
                    width: 2
                },
                itemStyle: { color },
                areaStyle: {
                    color: {
                        type: 'linear',
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,
                        colorStops: [
                            { offset: 0, color: this.withAlpha(color, 0.5) },
                            { offset: 1, color: this.withAlpha(color, 0.12) }
                        ]
                    }
                },
                symbol: 'circle',
                symbolSize: 7,
                showSymbol: false,
                emphasis: {
                    focus: 'series',
                    itemStyle: { color: '#FFFFFF', borderColor: color, borderWidth: 2 }
                }
            });

            // legendItems.push(`<span class="legend-item" style="display:inline-flex;align-items:center;margin-right:12px;font-size:12px;color:#bdbdbd"><i style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;flex:0 0 auto"></i><span style="white-space:nowrap">${seriesName}</span></span>`);
        }
        const marketListEl = this.shadowRoot.querySelector('.marketList');
        if (marketListEl) {
            // marketListEl.innerHTML = `<div class="legend-row" style="display:flex;align-items:center;gap:8px">${legendItems.join('')}</div>`;
        }
        // synchronize the small toggles in the options row with current selections
        try {
            const toggles = Array.from(this.shadowRoot.querySelectorAll('.toggle-date')) as HTMLElement[];
            toggles.forEach(t => {
                const v = t.getAttribute('data-value') || '';
                const active = (this.selectedDateSelections || []).indexOf(v) !== -1;
                t.classList.toggle('active', active);
                // t.setAttribute('aria-pressed', active.toString());
                // t.setAttribute('aria-selected', active.toString());
            });
        } catch (e) {
            // ignore
        }

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                ...getEChartsTooltipTheme(widgetBox),
                padding: [10, 14],
                axisPointer: {
                    type: 'line',
                    lineStyle: { color: textColor, width: 1, type: 'dashed' }
                },
                formatter: (params: any) => {
                    if (!params || params.length === 0) return '';
                    const rows = params.map((p: any) => {
                        const v = p.data;
                        const display = (v == null) ? '-' : this.formatYieldValue(v);
                        const subLabel = seriesData[p.seriesIndex]?.subLabel || p.axisValue;
                        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;margin-top:2px">
                                    <span style="display:flex;align-items:center;gap:8px">
                                        <span style="display:inline-block;width:9px;height:9px;border-radius:50%;border:2px solid ${p.color}"></span>
                                        <span style="font-weight:600">${p.seriesName}</span>
                                    </span>
                                    <span style="font-weight:600">${display}</span>
                                </div>
                                <div style="opacity:.65;font-size:12px;margin-bottom:4px">Range: ${subLabel}</div>`;
                    });
                    return rows.join('');
                }
            },
            grid: {
                top: '10',
                left: '0',
                right: '0',
                bottom: '0',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: maturities,
                axisLine: { show: false },
                axisLabel: { color: textColor, fontSize: 12 },
                axisTick: { show: false },
                splitLine: { show: true, lineStyle: { color: splitLineColor, type: 'dashed' } }
            },
            yAxis: {
                type: 'value',
                min: 'dataMin',
                axisLine: { show: false },
                splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } },
                axisLabel: {
                    color: textColor, fontSize: 12,
                    formatter: (v: any) => (v == null ? '' : this.formatYieldValue(v))
                }
            },
            series: seriesData
        };

        const chart = echarts.init(container);
        chart.setOption(option);
        this.currentChart = chart;
    }

    private formatYieldValue(value: number): string {
        return (value <= 1 ? (value * 100).toFixed(2) : value.toFixed(2)) + '%';
    }

    private withAlpha(hex: string, alpha: number): string {
        const normalized = hex.replace('#', '');
        const full = normalized.length === 3 ? normalized.split('').map(c => c + c).join('') : normalized;
        const r = parseInt(full.slice(0, 2), 16);
        const g = parseInt(full.slice(2, 4), 16);
        const b = parseInt(full.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }


    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any, stockInfo: any) => this.setupChart(stockId, stockInfo), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseCongressTradeTypeandAmountBreakdown: typeof TraderverseCongressTradeTypeandAmountBreakdown; } }
if (typeof window !== 'undefined') { window.TraderverseCongressTradeTypeandAmountBreakdown = TraderverseCongressTradeTypeandAmountBreakdown; }    